/**
 * Durable queue for watched-state writes.
 *
 * R6: an action is recorded on the device before any network call and survives
 * app close, offline, and server error until Simkl confirms it. The UI updates
 * immediately; this queue is what makes that optimism honest.
 *
 * Draining is serialised at Simkl's 1 POST/sec budget, which is also why the
 * queue matters: marking a 24-episode season is 24 seconds of writes, and the
 * user should be able to close the app halfway through.
 */
import type { SimklClient } from "@/api/client";
import { addToList, markUnwatched, markWatched, removeShow } from "@/api/writes";
import type { EpisodeTarget } from "@/api/writes";
import { OUTBOX_STORE, withStore } from "@/store/db";

export type OutboxAction =
  | { kind: "watch"; target: EpisodeTarget }
  | { kind: "unwatch"; target: EpisodeTarget }
  // Setting a position, not marking one episode. Simkl batches a whole list
  // into a single POST, so 90 episodes cost one request rather than ninety.
  | { kind: "watch-many"; targets: EpisodeTarget[] }
  | { kind: "unwatch-many"; targets: EpisodeTarget[] }
  | { kind: "add"; simklId: number; to: "watching" | "plantowatch" }
  | { kind: "remove"; simklId: number };

export interface OutboxEntry {
  id: string;
  action: OutboxAction;
  queuedAt: string;
  attempts: number;
  lastError: string | null;
}

/** Past this an entry stops retrying and is surfaced to the user instead. */
export const MAX_ATTEMPTS = 8;

/** Passes per drain. A guard against a queue that refills as fast as it empties. */
const MAX_PASSES = 10;

function targetKeys(targets: readonly EpisodeTarget[]): string {
  return targets
    .map((target) => `${String(target.simklId)}:${String(target.season)}:${String(target.episode)}`)
    .toSorted()
    .join(",");
}

/** The episode writes, as a comparable set. Anything else has no inverse. */
function episodeTargets(action: OutboxAction): { watched: boolean; keys: string } | null {
  switch (action.kind) {
    case "watch":
      return { watched: true, keys: targetKeys([action.target]) };
    case "unwatch":
      return { watched: false, keys: targetKeys([action.target]) };
    case "watch-many":
      return { watched: true, keys: targetKeys(action.targets) };
    case "unwatch-many":
      return { watched: false, keys: targetKeys(action.targets) };
    default:
      return null;
  }
}

/**
 * Cancels a queued write against its exact inverse.
 *
 * Marking a season and immediately unmarking it sent both, 250ms apart, and
 * Simkl did not apply them in that order — the episodes stayed watched.
 * Measured on a live account: sent 2, and the remove was the one that lost.
 *
 * Two opposing writes have no net effect, so the honest thing is not to race
 * them. The pair is dropped and nothing is sent at all.
 */
async function cancelInverse(action: OutboxAction): Promise<boolean> {
  const incoming = episodeTargets(action);
  if (incoming === null) return false;
  for (const entry of await pending()) {
    // An attempted entry may have reached Simkl before failing; dropping its
    // inverse would leave the account in the state the user just undid.
    if (entry.attempts > 0) continue;
    const queued = episodeTargets(entry.action);
    if (queued === null) continue;
    if (queued.keys === incoming.keys && queued.watched !== incoming.watched) {
      await remove(entry.id);
      return true;
    }
  }
  return false;
}

export async function enqueue(action: OutboxAction): Promise<OutboxEntry | null> {
  const entry: OutboxEntry = {
    id: crypto.randomUUID(),
    action,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  };
  if (await cancelInverse(action)) return null;
  await withStore(OUTBOX_STORE, "readwrite", (store) => store.put(entry));
  return entry;
}

export async function pending(): Promise<OutboxEntry[]> {
  const [entries] = await withStore<OutboxEntry[]>(OUTBOX_STORE, "readonly", (store) =>
    store.getAll(),
  );
  return (entries ?? []).toSorted((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

async function remove(id: string): Promise<void> {
  await withStore(OUTBOX_STORE, "readwrite", (store) => store.delete(id));
}

async function recordFailure(entry: OutboxEntry, error: unknown): Promise<void> {
  const updated: OutboxEntry = {
    ...entry,
    attempts: entry.attempts + 1,
    lastError: error instanceof Error ? error.message : "Unknown failure",
  };
  await withStore(OUTBOX_STORE, "readwrite", (store) => store.put(updated));
}

/** Targets carried by an episode action, or null for one that has none. */
function targetsOf(action: OutboxAction): EpisodeTarget[] | null {
  switch (action.kind) {
    case "watch":
    case "unwatch":
      return [action.target];
    case "watch-many":
    case "unwatch-many":
      return action.targets;
    default:
      return null;
  }
}

function direction(action: OutboxAction): boolean | null {
  const kind = action.kind;
  if (kind === "watch" || kind === "watch-many") return true;
  if (kind === "unwatch" || kind === "unwatch-many") return false;
  return null;
}

/** Episodes per request. Simkl takes a long list; a body still has a limit. */
const MAX_BATCH_TARGETS = 600;

export interface Batch {
  entries: OutboxEntry[];
  action: OutboxAction;
}

/**
 * The next request to send, which may cover several queued actions.
 *
 * Marking three seasons in a row used to be three requests. Simkl groups
 * episodes by show and season inside one body, so consecutive actions going
 * the same way are one request instead — fewer round trips against a
 * 1 POST/sec budget, and one request cannot be applied out of order with
 * itself, which is the hazard that left a season marked after being unmarked.
 *
 * Only a leading run of same-direction episode writes is merged. Anything
 * else is sent on its own, and order is preserved either way: entries between
 * two runs keep their place.
 */
export function nextBatch(entries: readonly OutboxEntry[]): Batch | null {
  const [first] = entries;
  if (first === undefined) return null;
  const heading = direction(first.action);
  const targets = targetsOf(first.action);
  if (heading === null || targets === null) return { entries: [first], action: first.action };

  const merged: OutboxEntry[] = [first];
  let all = [...targets];
  for (const entry of entries.slice(1)) {
    if (direction(entry.action) !== heading) break;
    const more = targetsOf(entry.action);
    if (more === null) break;
    if (all.length + more.length > MAX_BATCH_TARGETS) break;
    merged.push(entry);
    all = [...all, ...more];
  }
  return {
    entries: merged,
    action: heading ? { kind: "watch-many", targets: all } : { kind: "unwatch-many", targets: all },
  };
}

function apply(client: SimklClient, action: OutboxAction): Promise<void> {
  switch (action.kind) {
    case "watch":
      return markWatched(client, [action.target]);
    case "unwatch":
      return markUnwatched(client, [action.target]);
    case "watch-many":
      return markWatched(client, action.targets);
    case "unwatch-many":
      return markUnwatched(client, action.targets);
    case "add":
      return addToList(client, action.simklId, action.to);
    case "remove":
      return removeShow(client, action.simklId);
  }
}

export interface DrainResult {
  sent: number;
  failed: number;
  stuck: number;
}

/**
 * Sends queued actions in order.
 *
 * Order matters: watching then unwatching the same episode must not arrive
 * reversed. One drain runs at a time, and a failure stops the pass rather than
 * skipping ahead, so a later action cannot overtake an earlier one.
 */
export class OutboxDrainer {
  #running: Promise<DrainResult> | null = null;

  constructor(private readonly client: SimklClient) {}

  /**
   * Drains until nothing is left to send.
   *
   * Coalescing alone was a bug: a second call returned the in-flight drain's
   * promise, and that drain had already read the queue, so an action enqueued
   * a moment later was not in its batch. The caller was told "sent" and
   * refetched anyway, and the refetch reverted the change on screen. Two taps
   * in quick succession was all it took.
   *
   * So a caller waiting on a drain now waits for a pass that started after
   * its own entry was queued.
   */
  drain(): Promise<DrainResult> {
    this.#running ??= this.#runUntilEmpty().finally(() => {
      this.#running = null;
    });
    return this.#running;
  }

  async #runUntilEmpty(): Promise<DrainResult> {
    const total: DrainResult = { sent: 0, failed: 0, stuck: 0 };
    // Bounded: each pass either sends something or stops, so this cannot spin.
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      const result = await this.#run();
      total.sent += result.sent;
      total.failed += result.failed;
      total.stuck = result.stuck;
      if (result.sent === 0 || result.failed > 0) break;
      if ((await pending()).length === 0) break;
    }
    return total;
  }

  async #run(): Promise<DrainResult> {
    const result: DrainResult = { sent: 0, failed: 0, stuck: 0 };
    let queue = (await pending()).filter((entry) => {
      const stuck = entry.attempts >= MAX_ATTEMPTS;
      if (stuck) result.stuck += 1;
      return !stuck;
    });

    while (queue.length > 0) {
      const batch = nextBatch(queue);
      if (batch === null) break;
      try {
        await apply(this.client, batch.action);
        for (const entry of batch.entries) await remove(entry.id);
        result.sent += batch.entries.length;
      } catch (error) {
        // Stop the whole pass rather than skipping ahead. Order is the point:
        // a later unwatch must never overtake the watch it reverses. Every
        // entry in the batch is charged an attempt, because none of them went.
        for (const entry of batch.entries) await recordFailure(entry, error);
        result.failed += 1;
        break;
      }
      queue = queue.slice(batch.entries.length);
    }
    return result;
  }
}
